
import React, { useState, useEffect } from 'react';
import { GiphyFetch } from '@giphy/js-fetch-api';
import { Grid } from '@giphy/react-components';

const GIPHY_API_KEY = import.meta.env.VITE_GIPHY_API_KEY || 'PNsIrngaYlqv4xzdo3gFTgcDNTp5pGbZ';
const gf = new GiphyFetch(GIPHY_API_KEY);

interface GiphyPickerProps {
    onSelect: (url: string) => void;
    onClose: () => void;
}

const GiphyPicker: React.FC<GiphyPickerProps> = ({ onSelect, onClose }) => {
    const [search, setSearch] = useState('');
    const [tab, setTab] = useState<'gifs' | 'stickers'>('gifs');
    const [gridWidth, setGridWidth] = useState(window.innerWidth - 32);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        console.log("GiphyPicker: Mounted with API Key:", GIPHY_API_KEY.substring(0, 5) + "...");
        const handleResize = () => setGridWidth(window.innerWidth - 32);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchGifs = async (offset: number) => {
        try {
            setError(null);
            console.log(`GiphyPicker: Fetching ${tab} (search: "${search}", offset: ${offset})`);
            let result;
            if (search) {
                result = await gf.search(search, { offset, limit: 20, type: tab });
            } else {
                result = await gf.trending({ offset, limit: 20, type: tab });
            }
            console.log("GiphyPicker: Fetch result count:", result.data.length);
            return result;
        } catch (err) {
            console.error("GiphyPicker: Fetch error:", err);
            setError("Failed to load GIPHY content. Please check your connection.");
            throw err;
        }
    };

    return (
        <div className="fixed inset-0 z-[10000] bg-black/90 flex flex-col p-4 animate-fade-in backdrop-blur-sm">
            <div className="flex justify-between items-center mb-4 text-white">
                <div className="flex items-center gap-2">
                    <i className="ph-fill ph-gif text-3xl text-purple-500"></i>
                    <h2 className="text-xl font-bold tracking-tight">GIPHY</h2>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                    <i className="ph-bold ph-x text-2xl"></i>
                </button>
            </div>

            <div className="flex gap-2 mb-4 bg-white/5 p-1 rounded-xl">
                <button 
                    onClick={() => setTab('gifs')}
                    className={`flex-1 py-2 rounded-lg font-bold transition-all ${tab === 'gifs' ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/20' : 'text-gray-400 hover:text-white'}`}
                >
                    GIFs
                </button>
                <button 
                    onClick={() => setTab('stickers')}
                    className={`flex-1 py-2 rounded-lg font-bold transition-all ${tab === 'stickers' ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/20' : 'text-gray-400 hover:text-white'}`}
                >
                    Stickers
                </button>
            </div>

            <div className="relative mb-4">
                <input 
                    type="text" 
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={`Search ${tab}...`}
                    className="w-full bg-white/10 border-white/20 rounded-xl px-10 py-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all outline-none"
                />
                <i className="ph ph-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
                {search && (
                    <button 
                        onClick={() => setSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                        <i className="ph ph-x-circle text-xl"></i>
                    </button>
                )}
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0">
                {error ? (
                    <div className="flex flex-col items-center justify-center h-full text-center p-8">
                        <i className="ph ph-warning-circle text-4xl text-red-500 mb-2"></i>
                        <p className="text-gray-300">{error}</p>
                        <button 
                            onClick={() => setSearch(search)} // Trigger re-render
                            className="mt-4 text-purple-400 font-bold hover:underline"
                        >
                            Try Again
                        </button>
                    </div>
                ) : (
                    <Grid
                        key={`${search}-${tab}-${gridWidth}`}
                        width={gridWidth}
                        columns={2}
                        fetchGifs={fetchGifs}
                        onGifClick={(gif, e) => {
                            if (e && e.preventDefault) e.preventDefault();
                            const url = gif.images?.original?.url || gif.images?.fixed_height?.url;
                            if (url) {
                                onSelect(url);
                            } else {
                                console.error("No URL found for GIF", gif);
                            }
                        }}
                        gutter={8}
                    />
                )}
            </div>
            
            <div className="mt-4 pt-4 border-t border-white/10 text-center">
                <img src="https://raw.githubusercontent.com/Giphy/giphy-js/master/packages/react-components/static/Poweredby_640px-White_VertText.png" alt="Powered by GIPHY" className="h-6 mx-auto opacity-30" />
            </div>
        </div>
    );
};

export default GiphyPicker;
